"use client";

import { cn, isEmptyObject } from "@/lib/utils";
import { useCallback, useEffect, useRef, useState } from "react";

import MessageInput from "@/components/custom/comment/message-input";
import { CustomIcon } from "@/components/custom/icons/custom-icon";
import { Typography } from "@/components/custom/typography";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useAIChat } from "@/hooks/ai-chat-hooks/useAIChat";
import { useFreeCount } from "@/hooks/ai-chat-hooks/useGetUsage";
import { useReSearchStore } from "@/stores/reSearchStore";
import { useSearchConditionStore } from "@/stores/searchConditionStore";
import { useUser } from "@/stores/user";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import AIChatBubble from "./AIChatBubble";
import { AIChatHistories } from "./AIChatHistories";
import { Suggestions } from "./Suggestions";
import { environments } from "@/lib/config";
import { useMapUIStore } from "@/stores/mapUIStore";
import { useTypedTranslations } from "@/i18n/typed";
import Link from "next/link";

export type ChatMessage = {
  id: string;
  role: "user" | "assistant";
  text: string;
  thinking?: boolean;
};

export default function SearchAIChat() {
  const t = useTypedTranslations("searchAIChat");

  const { userInfo } = useUser();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [openAIChatHistories, setOpenAIChatHistories] = useState(false);
  const endRef = useRef<HTMLDivElement | null>(null);
  const router = useRouter();
  const searchParams = useSearchParams();
  const authServerUrl = environments.authServerUrl;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const pendingRecommended = useRef<any[]>([]);
  const { data: count = 0, refetch: refetchCount } = useFreeCount(
    userInfo?.user,
  );

  const isLogin = !isEmptyObject(userInfo?.user);
  const { mutate: aiChatMutate, isPending: aiChatPending } =
    useAIChat(refetchCount);

  const [isTyping, setIsTyping] = useState(false);
  const pathname = usePathname();
  const sessionIdRef = useRef<string>("");

  /** typing animation */
  const typingInterval = useRef<NodeJS.Timeout | null>(null);
  const typingQueue = useRef<string[]>([]);

  const { setCondition } = useSearchConditionStore();
  const { setHasSearched, setSearchCount } = useReSearchStore();
  const { openClusterList, hideSidebar } = useMapUIStore();

  /** Animate assistant typing effect */
  const startTyping = useCallback(
    (id: string) => {
      if (typingInterval.current) return;

      typingInterval.current = setInterval(() => {
        if (typingQueue.current.length === 0) {
          clearInterval(typingInterval.current!);
          typingInterval.current = null;
          setIsTyping(false);

          if (pendingRecommended.current.length > 0) {
            const params = new URLSearchParams(searchParams.toString());

            const mediaGroupIds: string[] = [];
            const mediaIds: string[] = [];

            for (const item of pendingRecommended.current) {
              if (item.media_group_o_id) {
                mediaGroupIds.push(String(item.media_group_o_id));
              }
              if (item.media_id) {
                mediaIds.push(String(item.media_id));
              }
            }

            if (mediaGroupIds.length > 0) {
              setCondition({ mediaGroupIds: mediaGroupIds.join(",") });
            }
            if (mediaIds.length > 0) {
              setCondition({ mediaId: mediaIds.join(",") });
            }

            router.replace(`?${params.toString()}`);
            openClusterList();
            setHasSearched(true);
            setSearchCount(pendingRecommended.current.length);
            pendingRecommended.current = [];
          }
          return;
        }

        const nextChar = typingQueue.current.shift()!;
        setMessages((prev) =>
          prev.map((m) =>
            m.id === id
              ? { ...m, text: m.text + nextChar, thinking: false }
              : m,
          ),
        );
      }, 20);
    },
    [
      openClusterList,
      router,
      searchParams,
      setCondition,
      setHasSearched,
      setSearchCount,
    ],
  );

  /** Send user message */
  const handleSend = (input: string) => {
    setMessages((prev) => [
      ...prev,
      { id: crypto.randomUUID(), role: "user", text: input },
    ]);

    const placeholderId = crypto.randomUUID();
    setMessages((prev) => [
      ...prev,
      { id: placeholderId, role: "assistant", text: "", thinking: true },
    ]);

    let firstResponse = true;
    let assistantId = placeholderId;

    aiChatMutate({
      input,
      isLogin,
      userId: isLogin ? userInfo?.user?.id || "" : "",
      sessionId: sessionIdRef.current,
      onChunk: (content, meta) => {
        if (meta?.session_id && !sessionIdRef.current) {
          sessionIdRef.current = meta.session_id;
        }

        if (meta?.recommendation_specific?.recommended_media?.length > 0) {
          pendingRecommended.current =
            meta.recommendation_specific.recommended_media;
        }

        if (!content) return;

        setIsTyping(true);

        if (firstResponse) {
          firstResponse = false;
          assistantId = crypto.randomUUID();

          setMessages((prev) => [
            ...prev.filter((m) => m.id !== placeholderId),
            { id: assistantId, role: "assistant", text: "", thinking: true },
          ]);

          startTyping(assistantId);
        }

        typingQueue.current.push(...content.split(""), "\n", "\n");
        startTyping(assistantId);
      },
    });
  };

  /** Reset chat */
  const handleNewPrompt = () => {
    if (aiChatPending || isTyping) return;

    sessionIdRef.current = "";
    setCondition({ mediaGroupIds: undefined, mediaId: undefined });
    setMessages([]);
    setOpenAIChatHistories(false);
    setHasSearched(false);
    hideSidebar();
  };

  /** Cleanup */
  useEffect(() => {
    return () => {
      if (typingInterval.current) clearInterval(typingInterval.current);
    };
  }, []);

  useEffect(() => {
    if (!openAIChatHistories) {
      endRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, openAIChatHistories]);

  useEffect(() => {
    if (pathname === "/") {
      setCondition({ mediaId: undefined, mediaGroupIds: undefined });
      hideSidebar();
    }
  }, [pathname, hideSidebar, setCondition]);

  return (
    <main className="flex flex-col flex-1 gap-2 overflow-hidden">
      {/* Top buttons */}
      <div className="flex justify-between shrink-0">
        <Button
          variant="secondary"
          tone="light"
          size="sm"
          onClick={() => setOpenAIChatHistories(true)}
          prefix={<CustomIcon name="ClockReverse" size={4} />}
          className="text-gray-700 px-2 py-1 gap-0.5 border border-gray-500 font-"
        >
          {t("chatHistory")}
        </Button>

        <Button
          variant="secondary"
          tone="light"
          size="sm"
          prefix={<CustomIcon name="EditPencil" size={4} />}
          onClick={handleNewPrompt}
          disabled={count === 0 || aiChatPending || isTyping}
          className="text-gray-700 px-2 py-1 gap-0.5 border border-gray-500"
        >
          {t("newChat")}
        </Button>
      </div>

      {!openAIChatHistories && (
        <div className="flex flex-col flex-1 overflow-y-auto">
          {/* Guest notice */}
          {messages.length === 0 && !openAIChatHistories && !isLogin && (
            <div className="flex flex-col flex-1 justify-center items-center gap-1">
              <Typography
                variant="body2"
                tone="medium"
                className="font-semibold"
              >
                {t("guestNoticeTitle")}
              </Typography>
              <div className="text-center">
                <Typography variant="detail1" tone="medium" as="span">
                  {t("guestNoticeLine1")}
                </Typography>
                <br />
                <Typography variant="detail1" tone="medium" as="span">
                  {t("guestNoticeLine2")}
                </Typography>
              </div>
              <Button
                appearance="ghost"
                tone="light"
                size="sm"
                className="!bg-primary-50"
                onClick={() => router.push(authServerUrl ?? "/account/login")}
              >
                {t("signupNow")}
              </Button>
            </div>
          )}

          {/* Messages */}
          {((messages.length > 0 && !openAIChatHistories) || isLogin) && (
            <ScrollArea className="flex-1 overflow-auto">
              <div className="space-y-2 mr-2">
                {messages.map((m) => (
                  <AIChatBubble key={m.id} message={m} />
                ))}
                <div ref={endRef} />
              </div>
            </ScrollArea>
          )}

          {/* Suggestions */}
          {messages.length === 0 && !openAIChatHistories && count > 0 && (
            <div>
              <Typography variant="detail1" as="span" tone="description">
                {t("exampleQuestions")}
              </Typography>
              <Suggestions onSelected={handleSend} className="space-y-2" />
            </div>
          )}
        </div>
      )}

      {/* Free user warning */}
      {!isLogin && !openAIChatHistories && (
        <div
          className={cn(
            "p-1 rounded-md flex justify-between shrink-0",
            count !== 0 ? "bg-red-100" : "bg-red-500",
          )}
        >
          <Typography
            as="span"
            variant="detail1"
            className="text-white font-medium"
          >
            {t("remainingChat", { count })}
          </Typography>
          <Typography
            as="span"
            variant="detail1"
            className="text-white font-medium"
          >
            <Link href={environments.authServerUrl ?? ""} className="underline underline-offset-2 mr-1">{t("loginForMore")}</Link>
            {t('moreSearch')}
          </Typography>
        </div>
      )}

      {/* Input */}
      {!openAIChatHistories && (
        <div className={cn("shrink-0", count === 0 && "opacity-50")}>
          <MessageInput
            onSend={handleSend}
            isHideUploadFile
            disabledSend={count === 0 || aiChatPending || isTyping}
            sendButtonPosition="bottom-2 right-2 rounded-full"
            placeholder={
              count > 0 ? t("inputPlaceholder") : t("inputPlaceholderExhausted")
            }
          />
        </div>
      )}

      {openAIChatHistories && (
        <div className="flex-1 overflow-auto">
          <AIChatHistories />
        </div>
      )}
    </main>
  );
}
